import type { StoreFragment } from "@server/gql/graphql";

type OpeningHoursValue = { hours?: string; closed: boolean };

export type WeeklyOpeningHoursLine = { days: string } & OpeningHoursValue;

export type SpecialOpeningHoursLine = OpeningHoursValue & {
  date: string;
  label?: string;
};

type RegularOpeningHoursDay = NonNullable<
  NonNullable<StoreFragment["openingHoursCollection"]>["items"][number]
>;

type ExceptionalOpeningHoursDay = NonNullable<
  NonNullable<StoreFragment["exceptionalOpeningHoursCollection"]>["items"][number]
>;

type OpeningHoursTimeFields = Pick<
  RegularOpeningHoursDay,
  "openingTime" | "closingTime" | "closed"
>;

const WEEKDAY_ORDER = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;

type WeekdayKey = (typeof WEEKDAY_ORDER)[number];

const WEEKDAY_INPUT_ALIASES: Record<string, WeekdayKey> = {
  mon: "mon",
  monday: "mon",
  tue: "tue",
  tuesday: "tue",
  wed: "wed",
  wednesday: "wed",
  thu: "thu",
  thursday: "thu",
  fri: "fri",
  friday: "fri",
  sat: "sat",
  saturday: "sat",
  sun: "sun",
  sunday: "sun",
};

const WEEKDAY_REFERENCE_DATES: Record<WeekdayKey, string> = {
  mon: "2024-01-01",
  tue: "2024-01-02",
  wed: "2024-01-03",
  thu: "2024-01-04",
  fri: "2024-01-05",
  sat: "2024-01-06",
  sun: "2024-01-07",
};

function normalizeWeekdayKey(value?: string | null, locale?: string) {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  const alias = WEEKDAY_INPUT_ALIASES[normalized];

  if (alias) {
    return alias;
  }

  const localesToTry = [
    locale,
    "sv-SE",
    "nb-NO",
    "da-DK",
    "fi-FI",
    "en-GB",
    "nl-NL",
  ].filter((entry, index, array): entry is string => {
    return Boolean(entry) && array.indexOf(entry) === index;
  });

  for (const tryLocale of localesToTry) {
    for (const weekday of WEEKDAY_ORDER) {
      const referenceDate = WEEKDAY_REFERENCE_DATES[weekday];
      const date = new Date(`${referenceDate}T00:00:00.000Z`);
      const longWeekday = new Intl.DateTimeFormat(tryLocale, {
        weekday: "long",
        timeZone: "UTC",
      })
        .format(date)
        .toLowerCase();
      const shortWeekday = new Intl.DateTimeFormat(tryLocale, {
        weekday: "short",
        timeZone: "UTC",
      })
        .format(date)
        .toLowerCase();

      if (
        normalized === longWeekday ||
        normalized === shortWeekday ||
        longWeekday.startsWith(normalized) ||
        shortWeekday.startsWith(normalized)
      ) {
        return weekday;
      }
    }
  }

  return null;
}

function extractIsoDate(value?: unknown) {
  if (!value) {
    return null;
  }

  const stringValue = String(value);
  const dateMatch = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);

  return dateMatch?.[1] ?? null;
}

function formatLocalizedTime(value: string | null | undefined, locale: string) {
  if (!value) {
    return undefined;
  }

  const timeMatch = value.match(/^(\d{1,2}):(\d{2})/);

  if (!timeMatch) {
    return value;
  }

  const hours = Number.parseInt(timeMatch[1] ?? "", 10);
  const minutes = Number.parseInt(timeMatch[2] ?? "", 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }

  const date = new Date(Date.UTC(2024, 0, 1, hours, minutes));

  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

function formatHoursRange(item: OpeningHoursTimeFields, locale: string) {
  if (item.closed) {
    return {
      closed: true,
    };
  }

  const open = formatLocalizedTime(item.openingTime, locale);
  const close = formatLocalizedTime(item.closingTime, locale);

  if (open && close) {
    return {
      hours: `${open}-${close}`,
      closed: false,
    };
  }

  return {
    closed: true,
  };
}

function formatLocalizedWeekday(
  weekday: WeekdayKey,
  locale: string,
  format: "short" | "long" = "short",
) {
  const referenceDate = WEEKDAY_REFERENCE_DATES[weekday];
  const localizedWeekday = new Intl.DateTimeFormat(locale, {
    weekday: format,
    timeZone: "UTC",
  }).format(new Date(`${referenceDate}T00:00:00.000Z`));

  return localizedWeekday.replace(/^\p{L}/u, (firstLetter) =>
    firstLetter.toLocaleUpperCase(locale),
  );
}

function formatWeekdayRangeLabel(
  startWeekday: WeekdayKey,
  endWeekday: WeekdayKey,
  locale: string,
  useLongWeekdayName = false,
) {
  const weekdayFormat = useLongWeekdayName ? "long" : "short";
  const start = formatLocalizedWeekday(startWeekday, locale, weekdayFormat);
  const end = formatLocalizedWeekday(endWeekday, locale, weekdayFormat);

  return startWeekday === endWeekday ? start : `${start}-${end}`;
}

function formatLocalizedDate(isoDate: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00.000Z`));
}

function getRegularOpeningHours(store: StoreFragment) {
  return (store.openingHoursCollection?.items ?? []).filter(
    (item): item is RegularOpeningHoursDay => item != null,
  );
}

function getExceptionalOpeningHours(store: StoreFragment) {
  return (store.exceptionalOpeningHoursCollection?.items ?? []).filter(
    (item): item is ExceptionalOpeningHoursDay => item != null,
  );
}

export function mapOpeningHours(
  store: StoreFragment,
  locale: string,
): {
  weekly: WeeklyOpeningHoursLine[];
  special: SpecialOpeningHoursLine[];
} {
  const regularOpeningHours = getRegularOpeningHours(store);
  const exceptionalOpeningHours = getExceptionalOpeningHours(store);

  const hoursByWeekday = new Map<WeekdayKey, OpeningHoursValue>();

  for (const openingHour of regularOpeningHours) {
    const weekdayKey = normalizeWeekdayKey(openingHour.dayOfTheWeek, locale);

    if (!weekdayKey) {
      continue;
    }

    hoursByWeekday.set(weekdayKey, formatHoursRange(openingHour, locale));
  }

  const orderedWeekdayHours = WEEKDAY_ORDER.map((weekday, index) => ({
    hours: hoursByWeekday.get(weekday),
    index,
    weekday,
  })).filter(
    (
      weekdayHours,
    ): weekdayHours is {
      index: number;
      hours: OpeningHoursValue;
      weekday: WeekdayKey;
    } => Boolean(weekdayHours.hours),
  );

  const hasSingleOpeningDay = orderedWeekdayHours.length === 1;
  const weeklyOpeningHours: WeeklyOpeningHoursLine[] = [];

  for (
    let rangeStartIndex = 0;
    rangeStartIndex < orderedWeekdayHours.length;
  ) {
    let rangeEndIndex = rangeStartIndex;
    const startRange = orderedWeekdayHours[rangeStartIndex];

    if (!startRange) {
      break;
    }

    while (rangeEndIndex + 1 < orderedWeekdayHours.length) {
      const currentRange = orderedWeekdayHours[rangeEndIndex];
      const nextRange = orderedWeekdayHours[rangeEndIndex + 1];

      if (!currentRange || !nextRange) {
        break;
      }

      const isConsecutiveDay = nextRange.index === currentRange.index + 1;
      const hasSameHours =
        nextRange.hours.hours === startRange.hours.hours &&
        nextRange.hours.closed === startRange.hours.closed;

      if (!isConsecutiveDay || !hasSameHours) {
        break;
      }

      rangeEndIndex++;
    }

    const endRange = orderedWeekdayHours[rangeEndIndex];

    if (!endRange) {
      break;
    }

    const isSingleDayRange = startRange.weekday === endRange.weekday;

    weeklyOpeningHours.push({
      days: formatWeekdayRangeLabel(
        startRange.weekday,
        endRange.weekday,
        locale,
        hasSingleOpeningDay || isSingleDayRange,
      ),
      hours: startRange.hours.hours,
      closed: startRange.hours.closed,
    });

    rangeStartIndex = rangeEndIndex + 1;
  }

  const specialOpeningHours = exceptionalOpeningHours
    .map((entry) => {
      const isoDate = extractIsoDate(entry.date);

      if (!isoDate) {
        return undefined;
      }

      const specialLine: SpecialOpeningHoursLine = {
        date: formatLocalizedDate(isoDate, locale),
        ...formatHoursRange(entry, locale),
      };

      const label = entry.title ?? entry.name;

      if (label) {
        specialLine.label = label;
      }

      return {
        sortKey: isoDate,
        line: specialLine,
      };
    })
    .filter(
      (entry): entry is { sortKey: string; line: SpecialOpeningHoursLine } =>
        Boolean(entry),
    )
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map((entry) => entry.line);

  return {
    weekly: weeklyOpeningHours,
    special: specialOpeningHours,
  };
}
